import React, { useState, useMemo, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import BlogCard from '../components/blog/BlogCard';
import { BlogPostMeta } from '../utils/dynamicBlogLoader';
import { Search } from 'lucide-react';
import axios from 'axios';
import { updateMetaTags, setCanonicalUrl } from '../utils/seoHelper';

const BlogPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [blogPosts, setBlogPosts] = useState<BlogPostMeta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [freeCreditsAmount, setFreeCreditsAmount] = useState<number>(10);
  const postsPerPage = 12;

  // Fetch free credits amount
  useEffect(() => {
    const fetchFreeCredits = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
        const response = await axios.get(`${apiUrl}/api/v1/site-settings/public`);
        if (response.data.success && response.data.data?.free_credits_amount) {
          const amount = parseInt(response.data.data.free_credits_amount);
          if (!isNaN(amount)) {
            setFreeCreditsAmount(amount);
          }
        }
      } catch (err) {
        console.error('Failed to fetch free credits:', err);
      }
    };
    fetchFreeCredits();
  }, []);

  // SEO - Update meta tags for blog list
  useEffect(() => {
    updateMetaTags({
      title: 'Blog | Metapic - AI Metadata Generator',
      description: 'Read articles and guides about stock photo metadata, AI keywords, and microstock earnings optimization.',
      url: `${window.location.origin}/blog`,
      type: 'website',
    });
    
    setCanonicalUrl(`${window.location.origin}/blog`);
  }, []);

  // Get all blog posts from API (tidak static files)
  useEffect(() => {
    const loadPosts = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
        const response = await axios.get(`${apiUrl}/api/v1/blog?limit=${postsPerPage}&page=${currentPage}`);
        
        // Handle both response formats:
        // 1. API returns: { success: true, data: { data: [...], pagination: {...} } }
        // 2. Direct array: [...]
        let postsData = [];
        let pagination = { pages: 1 };
        
        if (response.data.success && response.data.data) {
          // Format 1: nested structure
          postsData = response.data.data.data || response.data.data;
          pagination = response.data.data.pagination || { pages: 1 };
        } else if (Array.isArray(response.data)) {
          // Format 2: direct array
          postsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Format 3: data array
          postsData = response.data.data;
        }
        
        if (postsData.length > 0) {
          // Transform API response to BlogPostMeta format
          const posts = postsData.map((post: any) => ({
            slug: post.slug,
            title: post.title,
            description: post.description,
            date: post.created_at || post.date || new Date().toISOString(),
            category: post.category || '',
            tags: post.tags || [],
            image: post.image || '',
            published: post.published,
            created_at: post.created_at,
            author: post.author,
          }));
          setBlogPosts(posts);
          setTotalPages(pagination.pages || 1);
        }
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPosts();
  }, [currentPage]);

  // Get unique categories
  const categories = useMemo(
    () => ['All', ...new Set(blogPosts.map(post => post.category))],
    [blogPosts]
  );

  // Filter blog posts
  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesSearch = 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory = 
        selectedCategory === null || 
        selectedCategory === 'All' ||
        post.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [blogPosts, searchQuery, selectedCategory]);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header + Search + Filter - All in one line */}
        <section className="border-b border-gray-200 py-8 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-6">
              {/* Title */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Blog</h1>
                <p className="text-gray-600 mt-2">Articles and guides</p>
              </div>
              
              {/* Search */}
              <div className="flex-1 md:flex-none md:w-72">
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category === 'All' ? null : category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    (category === 'All' && selectedCategory === null) ||
                    (category !== 'All' && selectedCategory === category)
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts - Card Grid */}
        <section className="py-12 px-4">
          <div className="max-w-5xl mx-auto">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-600 mt-3">Loading...</p>
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map(post => (
                  <a
                    key={post.slug}
                    href={`/blog/${post.slug}`}
                    className="group block border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all bg-white"
                  >
                    {/* Card Image with Lazy Loading */}
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{
                          contentVisibility: 'auto',
                          backgroundColor: '#f3f4f6'
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No image</span>
                      </div>
                    )}
                    
                    {/* Card Content */}
                    <div className="p-5">
                      {/* Category */}
                      {post.category && (
                        <span className="inline-block text-xs font-medium px-3 py-1 bg-blue-50 text-blue-700 rounded-full mb-3">
                          {post.category}
                        </span>
                      )}
                      
                      {/* Title */}
                      <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 line-clamp-2 mb-2">
                        {post.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-gray-600 line-clamp-2 mb-4 text-sm leading-relaxed">
                        {post.description}
                      </p>
                      
                      {/* Meta */}
                      <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                        <span>
                          {post.created_at && new Date(post.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        {post.author && <span className="font-medium">{post.author}</span>}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">No posts found</p>
              </div>
            )}
            
            {/* Pagination */}
            {!isLoading && filteredPosts.length > 0 && totalPages > 1 && (
              <div className="mt-12 flex items-center justify-center gap-2">
                <button
                  onClick={() => {
                    setCurrentPage(Math.max(1, currentPage - 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === 1}
                  className="px-5 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => {
                        setCurrentPage(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                        currentPage === page
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => {
                    setCurrentPage(Math.min(totalPages, currentPage + 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === totalPages}
                  className="px-5 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default BlogPage;
